import { create } from 'zustand';
import { createContext, useContext, useRef, useCallback, useSyncExternalStore, useEffect } from 'react';
import agentSlice from './slices/agentSlice';
import chatSlice from './slices/chatSlice';

// Crear el contexto para el store
const StoreContext = createContext(null);

// Función para combinar múltiples slices
const createRootSlice = (set, get) => ({
  // Estado de autenticación
  userId: typeof window !== 'undefined' ? localStorage.getItem('vsise_user_id') : null,
  userProfile: null, // Perfil del usuario autenticado
  setUserProfile: (profile) => {
    set({ userProfile: profile });
  },
  setUserId: (id) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('vsise_user_id', id);
    }
    set({ userId: id });
  },

  // Estado de tema (claro por defecto)
  darkMode: typeof window !== 'undefined' 
    ? localStorage.getItem('darkMode') === 'true'
    : false,
    
  toggleDarkMode: () => set(state => {
    const newDarkMode = !state.darkMode;
    if (typeof window !== 'undefined') {
      localStorage.setItem('darkMode', newDarkMode);
      document.body.classList.toggle('dark', newDarkMode);
      document.body.classList.toggle('light', !newDarkMode);
    }
    return { darkMode: newDarkMode };
  }),

  // Estado de la UI
  sidebarOpen: true,
  toggleSidebar: () => set(state => ({ 
    sidebarOpen: !state.sidebarOpen 
  })),

  // Estado del Sidebar Derecho
  isRightSidebarOpen: true, // Por defecto abierto, o según preferencia
  toggleRightSidebar: () => set(state => ({ 
    isRightSidebarOpen: !state.isRightSidebarOpen 
  })),
  
  // Combinar con los slices
  ...agentSlice(set, get),
  ...chatSlice(set, get)
});

// Crear el store
const createStore = () => create(createRootSlice);

// Hook para usar el store
const useStore = (selector = (state) => state, equalityFn) => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useStore debe usarse dentro de un StoreProvider');
  }

  const selectorRef = useRef(selector);
  const equalityFnRef = useRef(equalityFn);
  
  selectorRef.current = selector;
  equalityFnRef.current = equalityFn;
  
  const getSnapshot = useCallback(() => {
    return selectorRef.current(store.getState());
  }, [store]);

  return useSyncExternalStore(
    store.subscribe,
    getSnapshot,
    typeof window === 'undefined' ? getSnapshot : undefined,
    equalityFnRef.current
  );
};

// Proveedor del store
const StoreProvider = ({ children, initialState }) => {
  const storeRef = useRef();
  
  // Inicializar el store solo una vez
  if (!storeRef.current) {
    console.log('Inicializando store...');
    storeRef.current = createStore();
    
    // Aplicar estado inicial si se proporciona
    if (initialState) {
      console.log('Aplicando estado inicial al store:', initialState);
      storeRef.current.setState(initialState);
    }
    
    // Agregar el store a window para depuración (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
      window.__STORE__ = storeRef.current;
    }
  }

  // Suscribirse a los cambios en el store para depuración
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && storeRef.current) {
      const unsubscribe = storeRef.current.subscribe((state) => {
        console.log('Store actualizado:', state);
      });
      
      return () => unsubscribe();
    }
  }, []);

  return (
    <StoreContext.Provider value={storeRef.current}>
      {children}
    </StoreContext.Provider>
  );
};

// Exportar el store y el hook
export { StoreProvider, useStore };
export default useStore;